import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import DepartmentLookupTable from './DepartmentLookupTable';
import NotificationTable from './NotificationTable'; // Import NotificationTable
import AdminHeader from '../../../../frontend/components/Admin/AdminHeader';
import { Box, Divider, Stack } from '@mui/material';
import TaskDependencyMapping from './TaskDependencyMapping/TaskDependencyMapping';
import EmailNotifications from './NotificationTemplates/EmailNotifications';
import WorkflowValidation from './WorkflowValidation/WorkflowValidation';
import AdminActivitiesTable from './AdminActivities/AdminActivitiesTable';
import APILogEntries from './APILogEntries';
import TaskTemplateSLAUpdates from './TaskTemplateQuickUpdates/TaskTemplateSLAUpdates';
import SEPDetails from './SEPDetails/SEPDetails';
import DataFieldMapping from './DataFieldMapping/DataFieldMapping';
import ValidationPhaseReset from './ValidationPhaseReset/ValidationPhaseReset';
import LogicAppCCEmails from './LogicAppCCEmails/LogicAppCCEmails';
import IconGallery from './IconGallery';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AiSummarySettingsTab from './AiSummarySettings/AiSummarySettingsTab';

const Others = () => {
  const [activeTab, setActiveTab] = useState('other-settings');
  const [aiSummaryDirty, setAiSummaryDirty] = useState(false);
  const [showTaskDependencyMapping, setShowTaskDependencyMapping] = useState<boolean>(false);
  const [showWorkflowValidation, setShowWorkflowValidation] = useState<boolean>(false);
  const [showEmailNotifications, setShowEmailNotifications] = useState<boolean>(false);
  const [showNotificationTable, setShowNotificationTable] = useState<boolean>(false);  
  const [showDepartmentLookupDetails, setShowDepartmentLookupDetails] = useState<boolean>(false);
  const [showAdminActivitiesTable, setShowAdminActivitiesTable] = useState<boolean>(false); // State for Admin Activities Table
  const [showAPILogEntries, setShowAPILogEntries] = useState<boolean>(false);  
  const [showTaskTemplateSLAUpdates, setShowTaskTemplateSLAUpdates] = useState<boolean>(false);
  const [ showSEPDetails, setShowSEPDetails] = useState<boolean>(false);
  const [showDataFieldMapping, setShowDataFieldMapping] = useState<boolean>(false);
  const [showValidationTasks, setShowValidationTasks] = useState<boolean>(false);
  const [showLogicAppCCEmails, setShowLogicAppCCEmails] = useState<boolean>(false);
  const [showIconGallery, setShowIconGallery] = useState<boolean>(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (activeTab === 'ai-summary' && newValue !== 'ai-summary' && aiSummaryDirty) {
      if (!window.confirm('You have unsaved AI summary changes. Leave this tab?')) return;
    }
    setActiveTab(newValue);
  };

  return (
    <div>
      <div className='mb-4'>
        <AdminHeader title="SEP Other Settings" />
      </div>

      <Tabs value={activeTab} onChange={handleTabChange} aria-label="Other settings tabs">
        <Tab label="Other settings" value="other-settings" />
        <Tab label="AI summary" value="ai-summary" />
      </Tabs>

      {activeTab === 'ai-summary' ? (
        <AiSummarySettingsTab onDirtyChange={setAiSummaryDirty} />
      ) : (
      <div>

      {/* Task Dependency Mapping */}
      <Box
        data-testid="task-dependency-mapping-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Task Dependency Mapping
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showTaskDependencyMapping ? 'up' : 'down'}`}
              onClick={() => setShowTaskDependencyMapping(!showTaskDependencyMapping)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showTaskDependencyMapping && (
        <Stack sx={{ p: 3 }}>
          <TaskDependencyMapping />
        </Stack>
      )}

       {/* Validation Tasks  */}
      <Box
        data-testid="validation-tasks-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
               Validation Phase Bulk Update
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showValidationTasks ? 'up' : 'down'}`}
              onClick={() => setShowValidationTasks(!showValidationTasks)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showValidationTasks && (
        <Stack sx={{ p: 3 }}>
          <ValidationPhaseReset />
        </Stack>
      )}

      {/* SEP Details */}
      <Box
        data-testid="sep-details-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              SEP Details
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showSEPDetails ? 'up' : 'down'}`}
              onClick={() => setShowSEPDetails(!showSEPDetails)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showSEPDetails && (
        <Stack sx={{ p: 3 }}>
          <SEPDetails />
        </Stack>
      )}

      {/* Data Field Mapping */}
      <Box
        data-testid="data-field-mapping-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Data Field Mapping (Add to All SEPs)
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showDataFieldMapping ? 'up' : 'down'}`}
              onClick={() => setShowDataFieldMapping(!showDataFieldMapping)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showDataFieldMapping && (
        <Stack sx={{ p: 3 }}>
          <DataFieldMapping />
        </Stack>
      )}

      {/* Task Template SLA Updates */}
      <Box
        data-testid="task-template-sla-updates-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Task Template SLA Updates
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showTaskTemplateSLAUpdates ? 'up' : 'down'}`}
              onClick={() => setShowTaskTemplateSLAUpdates(!showTaskTemplateSLAUpdates)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showTaskTemplateSLAUpdates && (
        <Stack sx={{ p: 3 }}>
          <TaskTemplateSLAUpdates />
        </Stack>
      )}


      {/* Logic App CC Emails */}
      <Box
        data-testid="logic-app-cc-emails-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Logic App CC Emails
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showLogicAppCCEmails ? 'up' : 'down'}`}
              onClick={() => setShowLogicAppCCEmails(!showLogicAppCCEmails)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showLogicAppCCEmails && (
        <Stack sx={{ p: 3 }}>
          <LogicAppCCEmails />
        </Stack>
      )}

      {/* Notification Template Configuration */}
      <Box
        data-testid="notification-template-configuration-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Email Notifications
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showEmailNotifications ? 'up' : 'down'}`}
              onClick={() => setShowEmailNotifications(!showEmailNotifications)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showEmailNotifications && (
        <Stack sx={{ p: 3 }}>
          <EmailNotifications />
        </Stack>
      )}

      <Box
        data-testid="department-lookup-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Box alignItems="center">
            <Typography
              component="span"
              sx={{
                fontSize: typographyTokens.fontSizes.xl,
                fontWeight: typographyTokens.fontWeights.bold,
              }}
            >
              Department Lookups
            </Typography>
            &nbsp;&nbsp;
            {(
              <i
                className={`fa-sharp fa-solid fa-chevron-${showDepartmentLookupDetails ? 'up' : 'down'
                  }`}
                onClick={() => setShowDepartmentLookupDetails(!showDepartmentLookupDetails)}
              ></i>
            )}
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showDepartmentLookupDetails && (
        <Stack sx={{ p: 3 }}>
          <DepartmentLookupTable />
        </Stack>
      )}

      {/* Workflow Validation */}
      <Box
        data-testid="workflow-validation-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Workflow Validation
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showWorkflowValidation ? 'up' : 'down'}`}
              onClick={() => setShowWorkflowValidation(!showWorkflowValidation)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showWorkflowValidation && (
        <Stack sx={{ p: 3 }}>
          <WorkflowValidation />
        </Stack>
      )}
      
      {/* Notification Table */}
      <Box
        data-testid="notification-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Notification Logs
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showNotificationTable ? 'up' : 'down'}`}
              onClick={() => setShowNotificationTable(!showNotificationTable)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showNotificationTable && (
        <Stack sx={{ p: 3 }}>
          <NotificationTable />
        </Stack>
      )}

      {/* Admin Activity Logs Table */}
      <Box
        data-testid="admin-activity-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Admin Activity Logs
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showAdminActivitiesTable ? 'up' : 'down'}`}
              onClick={() => setShowAdminActivitiesTable(!showAdminActivitiesTable)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showAdminActivitiesTable && (
        <Stack sx={{ p: 3 }}>
          <AdminActivitiesTable />
        </Stack>
      )}

      {/* API Log Entries Table */}
      <Box
        data-testid="api-log-entries-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              API Log Entries
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showAPILogEntries ? 'up' : 'down'}`}
              onClick={() => setShowAPILogEntries(!showAPILogEntries)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showAPILogEntries && (
        <Stack sx={{ p: 3 }}>
          <APILogEntries />
        </Stack>
      )}

      {/* Icon Gallery */}
      <Box
        data-testid="icon-gallery-screen-card"
        sx={{
          borderLeft: 'solid 5px #2372B9',
          padding: '30px 40px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        <Stack display="flex" flexDirection="row" justifyContent="space-between">
          <Box alignItems="center">
            <Typography component="span" sx={{ fontSize: typographyTokens.fontSizes.xl, fontWeight: typographyTokens.fontWeights.bold }}>
              Icon Gallery
            </Typography>
            <i
              className={`fa-sharp fa-solid fa-chevron-${showIconGallery ? 'up' : 'down'}`}
              onClick={() => setShowIconGallery(!showIconGallery)}
              style={{ cursor: 'pointer', marginLeft: 8 }}
            ></i>
          </Box>
        </Stack>
      </Box>
      <Divider />
      {showIconGallery && (
        <Stack sx={{ p: 3 }}>
          <IconGallery />
        </Stack>
      )}
      </div>
      )}
    </div>
  );
}

export default Others;


